import {gql} from 'graphql-request';
import {useEffect} from 'react';
import {useGqlRequest} from '@/api/request';
import {useQuery} from '@tanstack/react-query';
import {useDebounce, useThrottle} from '@uidotdev/usehooks';
import {AddressSearchResult, Coord2AddressData} from '../@types';

export const useCoordToAddress = (coord?: {lng: number; lat: number}, debounceMs = 0) => {
  const request = useGqlRequest({
    query: COORD_TO_ADDRESS,
  });
  const debouncedCoord = useDebounce(coord, debounceMs);
  const activeCoord = debounceMs > 0 ? debouncedCoord : coord;

  const queryFn = ():
    | undefined
    | Promise<{
        result?: Coord2AddressData;
      }> => {
    if (!activeCoord) {
      return;
    }
    return request(activeCoord);
  };

  const select = (data?: {result?: Coord2AddressData}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['coord2addr', activeCoord],
    enabled: !!activeCoord,
    select,
  });
};

export const useSearchAddress = (value?: string) => {
  const request = useGqlRequest({
    query: SEARCH_ADDRESS_BY_KEYWORD,
  });
  const keyword = useThrottle(value, 500);

  useEffect(() => {}, [value]);

  const queryFn = ():
    | undefined
    | Promise<{
        result: AddressSearchResult;
      }> => {
    if (!keyword?.length) {
      return;
    }

    return request({keyword, limit: 5});
  };

  const select = (data?: {result?: AddressSearchResult}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['search-address', keyword],
    enabled: !!keyword?.length,
    select,
  });
};

const COORD_TO_ADDRESS = gql`
  query coord2Address($lng: Float!, $lat: Float!) {
    result: coord2Address(lng: $lng, lat: $lat) {
      address
      lat
      lng
    }
  }
`;

const SEARCH_ADDRESS_BY_KEYWORD = gql`
  query searchLocationByKeyboard($keyword: String!) {
    result: searchLocationByKeyboard(keyword: $keyword) {
      address {
        addressName
        location {
          type
          coordinates
        }
        placeName
        roadAddressName
      }
      subway {
        line
        location {
          type
          coordinates
        }
        name
        organization
      }
    }
  }
`;
